/**
 * Simplified persistence middleware using Zustand's built-in persist
 * Provides localStorage persistence with configurable options
 */

import { persist, createJSONStorage, PersistOptions } from 'zustand/middleware'
import { StateCreator } from 'zustand'

/**
 * Creates a localStorage persistence config
 * @param name - Storage key name
 * @param options - Additional persist options
 * @returns Persistence middleware
 */
export function createLocalStorageConfig<T>(
  name: string,
  options?: Partial<PersistOptions<T>>
) {
  return persist<T>(
    // The state creator will be passed in when used
    {} as any,
    {
      name,
      storage: createJSONStorage(() => localStorage),
      ...options
    }
  )
}

/**
 * Creates a sessionStorage persistence config
 * @param name - Storage key name
 * @param options - Additional persist options
 * @returns Persistence middleware
 */
export function createSessionStorageConfig<T>(
  name: string,
  options?: Partial<PersistOptions<T>>
) {
  return persist<T>(
    // The state creator will be passed in when used
    {} as any,
    {
      name,
      storage: createJSONStorage(() => sessionStorage),
      ...options
    }
  )
}

/**
 * Default persistence configuration
 */
export const defaultPersistConfig = {
  storage: createJSONStorage(() => localStorage),
}

/**
 * Creates a persistence middleware for Zustand stores
 * @param name - Storage key name
 * @param options - Persistence options
 * @returns Middleware function
 */
export function createPersistMiddleware<T>(
  name: string,
  options?: Partial<PersistOptions<T>>
) {
  return (stateCreator: StateCreator<T>) => 
    persist(stateCreator, {
      name,
      // @ts-ignore
      storage: createJSONStorage(() => localStorage),
      ...options
    })
}